import React, { useState, useRef } from 'react';
import { X, Upload, Bug, Lightbulb, ImagePlus, Trash2, Send, Loader2 } from 'lucide-react';
import api from '../services/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Resolve screenshot URL — handles data: URIs (base64), relative paths, and absolute URLs
const fullUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:')) return url;           // base64 — use as-is
  if (url.startsWith('http')) return url;             // absolute — use as-is
  return `${API_BASE}${url}`;                         // relative — prepend backend URL
};

export default function FeedbackForm({ onClose }) {
  const [type, setType] = useState('bug');
  const [description, setDescription] = useState('');
  const [screenshots, setScreenshots] = useState([]); // [{url, name}]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files.slice(0, 3)) {
        const form = new FormData();
        form.append('file', file);
        const res = await api.post('/feedback/upload-screenshot', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setScreenshots((prev) => [...prev, { url: res.data.url, name: file.name }]);
      }
    } catch {
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!description.trim()) { setError('Please describe the issue or suggestion.'); return; }
    setSubmitting(true);
    setError('');
    try {
      await api.post('/feedback', {
        type,
        description: description.trim(),
        screenshot_urls: screenshots.map((s) => s.url),
        page_context: window.location.pathname,
      });
      setSubmitted(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-[#075E54] text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {type === 'bug' ? <Bug className="w-5 h-5" /> : <Lightbulb className="w-5 h-5" />}
            <span className="font-semibold">{type === 'bug' ? 'Report a Bug' : 'Suggest an Improvement'}</span>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {submitted ? (
          <div className="p-8 text-center space-y-3">
            <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
              <Send className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-gray-800">Thank you!</h3>
            <p className="text-sm text-gray-500">Your feedback has been submitted. Admin will review and get back to you.</p>
            <button onClick={onClose} className="mt-2 text-sm text-emerald-600 font-medium underline">Close</button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {/* Type toggle */}
            <div className="flex gap-2">
              {[['bug', 'Bug Report', Bug], ['feature', 'Suggestion', Lightbulb]].map(([val, label, Icon]) => (
                <button
                  key={val}
                  onClick={() => setType(val)}
                  data-testid={`feedback-type-${val}`}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    type === val ? 'bg-[#075E54] text-white border-[#075E54]' : 'border-gray-200 text-gray-600 hover:border-[#075E54]'
                  }`}
                >
                  <Icon className="w-4 h-4" /> {label}
                </button>
              ))}
            </div>

            {/* Description */}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                {type === 'bug' ? 'Describe the bug (what happened, what you expected)' : 'Describe your suggestion'}
              </label>
              <textarea
                value={description}
                onChange={(e) => { setDescription(e.target.value); setError(''); }}
                rows={4}
                placeholder={type === 'bug' ? 'e.g. When I click Save on the lead status, it shows a blank screen...' : 'e.g. It would be helpful to see total sales by city on the dashboard...'}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#075E54]/30 resize-none"
                data-testid="feedback-description"
              />
            </div>

            {/* Screenshots */}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Screenshots (optional, max 3)</label>
              <div className="flex flex-wrap gap-2">
                {screenshots.map((s, i) => (
                  <div key={i} className="relative group w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                    <img src={fullUrl(s.url)} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => setScreenshots((prev) => prev.filter((_, j) => j !== i))}
                      className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                    >
                      <Trash2 className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ))}
                {screenshots.length < 3 && (
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid="feedback-upload-btn"
                    className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-[#075E54] hover:text-[#075E54] transition-colors disabled:opacity-50"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                    <span className="text-[10px] mt-0.5">{uploading ? '' : 'Add'}</span>
                  </button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} data-testid="feedback-file-input" />
            </div>

            {error && <p className="text-xs text-red-500" data-testid="feedback-error">{error}</p>}

            <button
              onClick={handleSubmit}
              disabled={submitting || !description.trim()}
              data-testid="feedback-submit-btn"
              className="w-full py-3 bg-[#075E54] text-white rounded-xl font-semibold text-sm hover:bg-[#064E46] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? 'Submitting…' : 'Submit Feedback'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
